import React from "react";
import {
  SIGN_PDF_BLOCK_CATEGORIES,
  encodeSignPdfBlockPayload,
} from "./signPdfBlockCatalog";
import { SIGN_PDF_BLOCK_MIME, SIGN_PDF_BLOCK_PRESETS } from "./signPdfPlacements";
import "./SignPdfBlocksPanel.css";

function BlockPreview({ variant = "name-sig-date", align = "right" }) {
  const showName = variant === "name-sig-date";
  return (
    <span
      className={`sp-blocks-card__preview sp-blocks-card__preview--${align}`}
      aria-hidden="true"
    >
      {showName ? (
        <span className="sp-blocks-card__line sp-blocks-card__line--name" />
      ) : null}
      <span className="sp-blocks-card__line sp-blocks-card__line--sig" />
      <span className="sp-blocks-card__line sp-blocks-card__line--date" />
    </span>
  );
}

function BlockCard({
  block,
  disabled,
  canPlace,
  onPlace,
}) {
  const tryPlace = (sizeId) => {
    if (disabled) return;
    if (!canPlace) {
      onPlace?.(block, sizeId, { needSignature: true });
      return;
    }
    onPlace?.(block, sizeId);
  };

  const onDragStart = (e) => {
    if (disabled || !canPlace) {
      e.preventDefault();
      return;
    }
    const sizeId = block.defaultSizeId || "standard";
    e.dataTransfer.setData(
      SIGN_PDF_BLOCK_MIME,
      encodeSignPdfBlockPayload(block.id, sizeId)
    );
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div
      className="sp-blocks-card"
      draggable={!disabled && canPlace}
      onDragStart={onDragStart}
    >
      <button
        type="button"
        className="sp-blocks-card__main"
        disabled={disabled}
        title={`Place ${block.label}`}
        onClick={() => tryPlace(block.defaultSizeId || "standard")}
      >
        <BlockPreview
          variant={block.preview}
          align={block.previewAlign || "right"}
        />
        <span className="sp-blocks-card__copy">
          <span className="sp-blocks-card__label">{block.label}</span>
          <span className="sp-blocks-card__meta">{block.description}</span>
        </span>
      </button>

      <div
        className="sp-blocks-card__sizes"
        role="group"
        aria-label={`${block.label} sizes`}
      >
        {SIGN_PDF_BLOCK_PRESETS.map((size) => (
          <button
            key={size.id}
            type="button"
            className="sp-blocks-card__size"
            disabled={disabled}
            title={size.description}
            draggable={!disabled && canPlace}
            onDragStart={(e) => {
              if (disabled || !canPlace) {
                e.preventDefault();
                return;
              }
              e.stopPropagation();
              e.dataTransfer.setData(
                SIGN_PDF_BLOCK_MIME,
                encodeSignPdfBlockPayload(block.id, size.id)
              );
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={(e) => {
              e.stopPropagation();
              tryPlace(size.id);
            }}
          >
            {size.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Domain signature blocks — toolbar flyout or legacy overlay host.
 */
const SignPdfBlocksPanel = ({
  open = false,
  variant = "overlay",
  onClose = () => {},
  disabled = false,
  canPlace = false,
  onPlace,
}) => {
  if (!open) return null;

  const isFlyout = variant === "flyout";

  return (
    <div
      className={`sp-blocks-panel${
        isFlyout ? " sp-blocks-panel--flyout" : ""
      }`}
      role={isFlyout ? "menu" : "dialog"}
      aria-label="Signature blocks"
    >
      <header className="sp-blocks-panel__header">
        <div className="sp-blocks-panel__titles">
          <h3 className="sp-blocks-panel__title">Signature Blocks</h3>
          <p className="sp-blocks-panel__hint">
            Pick a block, then Compact / Standard / Spacious
          </p>
        </div>
        {!isFlyout ? (
          <button
            type="button"
            className="sp-blocks-panel__close"
            onClick={onClose}
            aria-label="Close signature blocks"
            title="Close"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4L4 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
      </header>

      <div className="sp-blocks-panel__body">
        {SIGN_PDF_BLOCK_CATEGORIES.map((category) => (
          <section key={category.id} className="sp-blocks-section">
            <h4 className="sp-blocks-section__label">{category.label}</h4>
            <div className="sp-blocks-section__grid">
              {(category.blocks || []).map((block) => (
                <BlockCard
                  key={block.id}
                  block={block}
                  disabled={disabled}
                  canPlace={canPlace}
                  onPlace={onPlace}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default SignPdfBlocksPanel;
